import { useState } from 'react'

import { RowData, createColumnHelper } from '@tanstack/react-table'
import { BiDoorOpen, BiFilm, BiVideo } from 'react-icons/bi'
import { FaFlag } from 'react-icons/fa'
import { IoMdQrScanner } from 'react-icons/io'
import { TbClipboardText } from 'react-icons/tb'

import {
  Avatar,
  Badge,
  Box,
  Checkbox,
  HStack,
  Icon,
  Text,
  Tooltip,
} from '@chakra-ui/react'

import { AiOperator, Alarm, Guards, PriorityIcon } from '@/components/icons'
import { HeaderCell, TableCell } from '@/components/ui'
import {
  DeviceType,
  IncidentPriority,
  IncidentSource,
} from '@/graphql/generated/schemas'
import { formatDateTime, formatTimeDistance } from '@/utils/formatDateTime'
import { getArchivedLabel, getArchivedLabelWithSN } from '@/utils/formats'
import {
  getIncidentPriorityDisplayName,
  getIncidentSourceDisplayName,
  getIncidentStatusColor,
  getIncidentStatusDisplayName,
  isAIOperator,
} from '@/utils/incidents'

import {
  IncidentOrigin,
  IncidentRowDataType,
  IncidentStatusOwner,
} from '../types/types'
import { IncidentGuardCell } from './IncidentGuardCell'

export const IS_FLAGGED_COL_NAME = 'isFlagged'
export const PRIORITY_COL_NAME = 'priority'
export const BULK_ACTION_CHECKBOX_COL_ID = 'bulkActionCheckbox'

export const Source = ({ source }: { source?: IncidentOrigin }) => {
  switch (source.type) {
    case DeviceType.Alarm:
      return <Icon as={Alarm} boxSize={5} strokeColor='black' />
    case DeviceType.Door:
      return (
        <BiDoorOpen color='black' data-testid='source-door-cell' size={20} />
      )
    case DeviceType.Camera:
      return (
        <BiVideo color='black' data-testid='source-camera-cell' size={20} />
      )
    case IncidentSource.Guard:
      return <Icon as={Guards} boxSize={5} strokeColor='black' />
    case IncidentSource.QuickReport:
      return (
        <IoMdQrScanner color='black' data-testid='source-qr-cell' size={20} />
      )

    default:
      return <TbClipboardText color='black' size={20} />
  }
}

export const Status = ({
  incidentStatusOwner,
}: {
  incidentStatusOwner?: IncidentStatusOwner
}) => {
  const { owner, status, isFlagged, hasVideoClip } = incidentStatusOwner
  const shouldShowAIOperatorIcon = isAIOperator(owner) || isFlagged
  const shouldShowClipIcon = hasVideoClip && !shouldShowAIOperatorIcon
  const statusLabel = getIncidentStatusDisplayName(status, isFlagged)
  const [tooltipContext, setTooltipContext] = useState<
    'status' | 'clip' | 'ai'
  >('status')

  const tooltipLabel =
    tooltipContext === 'clip'
      ? 'Incident has video clip'
      : tooltipContext === 'ai'
      ? 'Reviewed by AI Operator'
      : statusLabel

  return (
    <Tooltip hasArrow label={tooltipLabel} openDelay={500} placement='auto'>
      <Badge
        alignItems='center'
        bgColor={getIncidentStatusColor(status)}
        color='#fff'
        data-testid='incidentsPage_table_statusCell'
        display='flex'
        fontSize='14px'
        px='10px'
        py='4px'
        rounded='10px'
        textTransform='capitalize'
        w='max-content'
      >
        {shouldShowClipIcon && (
          <Box
            alignItems='center'
            display='inline-flex'
            mr='4px'
            onMouseEnter={() => setTooltipContext('clip')}
            onMouseLeave={() => setTooltipContext('status')}
          >
            <BiFilm
              color='white'
              data-testid='incidentsPage_table_statusVideoClipIcon'
              size={18}
            />
          </Box>
        )}
        {shouldShowAIOperatorIcon && (
          <Box
            alignItems='center'
            data-testid='incidentsPage_table_statusAIOperatorIcon'
            display='inline-flex'
            mr='4px'
            onMouseEnter={() => setTooltipContext('ai')}
            onMouseLeave={() => setTooltipContext('status')}
          >
            <Icon as={AiOperator} boxSize={4} strokeColor='white' />
          </Box>
        )}
        {statusLabel}
      </Badge>
    </Tooltip>
  )
}

export const Priority = ({ priority }: { priority?: IncidentPriority }) => {
  const displayName = getIncidentPriorityDisplayName(priority)
  const tooltipLabel = priority ? `${displayName} Priority` : displayName

  return (
    <Tooltip hasArrow label={tooltipLabel} openDelay={500} placement='auto'>
      <Box data-testid='incidentsPage_table_priorityCell'>
        <PriorityIcon priority={priority} showTextIfNoPriority={false} />
      </Box>
    </Tooltip>
  )
}

const columnHelper = createColumnHelper<IncidentRowDataType>()

export const columns = [
  columnHelper.display({
    id: BULK_ACTION_CHECKBOX_COL_ID,
    meta: {
      // We want to ignore clicks to this CELL that aren't checkbox clicks
      // i.e. the user missed the checkbox, but was still close and in the cell
      ignoreClicksToCell: true,
    },
    header: ({ table }) => (
      <Checkbox
        borderColor='black'
        data-testid='incidentsPage_table_selectAllCheckbox'
        isChecked={table.getIsAllRowsSelected()}
        isIndeterminate={table.getIsSomeRowsSelected()}
        onChange={table.getToggleAllRowsSelectedHandler()}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        data-testid={`incidentsPage_table_selectRowCheckbox_${row.id}`}
        isChecked={row.getIsSelected()}
        onChange={() => {
          row.toggleSelected()
        }}
      />
    ),
    enableSorting: false,
    maxSize: 10,
  }),
  columnHelper.accessor(IS_FLAGGED_COL_NAME, {
    header: () => <HeaderCell label='' />,
    cell: ({ getValue }) => (
      <Tooltip
        hasArrow
        isDisabled={!getValue()}
        label='AI Flagged Incident'
        openDelay={500}
        placement='auto'
      >
        <Box>
          {getValue() && (
            <FaFlag
              color='#D01030'
              data-testid='incidentsPage_table_flaggedCell'
              size={18}
            />
          )}
        </Box>
      </Tooltip>
    ),
    enableSorting: false,
    minSize: 0,
    maxSize: 10,
  }),
  columnHelper.accessor(PRIORITY_COL_NAME, {
    header: ({ header }) => (
      <HeaderCell
        canSort={header.column.getCanSort()}
        data-testid='incidentsPage_table_priorityHeaderCell'
        getIsSorted={header.column.getIsSorted}
        label=''
      />
    ),
    cell: ({ getValue }) => <Priority priority={getValue()} />,
    enableSorting: true,
    size: 20,
    sortDescFirst: false,
  }),
  columnHelper.accessor('displayId', {
    header: ({ header }) => (
      <HeaderCell
        canSort={header.column.getCanSort()}
        getIsSorted={header.column.getIsSorted}
        label='INC ID'
      />
    ),
    cell: ({ getValue }) => (
      <TableCell
        dataTestId='incidentsPage_table_displayIdCell'
        label={getValue()}
      />
    ),
    enableSorting: true,
    maxSize: 30,
  }),
  columnHelper.accessor('date', {
    header: ({ header }) => (
      <HeaderCell
        canSort={header.column.getCanSort()}
        getIsSorted={header.column.getIsSorted}
        label='Date'
      />
    ),
    cell: ({ getValue }) => {
      return (
        <TableCell
          dataTestId='incidentsPage_table_dateCell'
          fontWeight={400}
          isTruncated
          label={formatDateTime(getValue())}
          tooltipLabel={`${formatDateTime(getValue())} • ${formatTimeDistance(
            getValue()
          )}`}
          whiteSpace='pre-line'
        />
      )
    },
    enableSorting: true,
  }),
  columnHelper.accessor('incidentName', {
    header: ({ header }) => (
      <HeaderCell
        canSort={header.column.getCanSort()}
        getIsSorted={header.column.getIsSorted}
        label='Name'
      />
    ),
    cell: ({ getValue }) => (
      <TableCell
        dataTestId='incidentsPage_table_incidentNameCell'
        label={getValue()}
        maxWidth='200px'
        noOfLines={2}
        whiteSpace='pre-line'
        width='max-content'
      />
    ),
    enableSorting: true,
  }),
  columnHelper.accessor('facility', {
    header: ({ header }) => (
      <HeaderCell
        canSort={header.column.getCanSort()}
        getIsSorted={header.column.getIsSorted}
        label='Facility'
      />
    ),
    cell: ({ getValue }) => (
      <Tooltip
        hasArrow
        label={getArchivedLabelWithSN(getValue())}
        openDelay={500}
        placement='auto'
      >
        <Text
          bg='rgba(9, 30, 66, 0.03)'
          borderRadius='100px'
          color='black'
          data-testid='incidentsPage_table_facilityNameCell'
          fontWeight='normal'
          isTruncated
          maxWidth='200px'
          px='8px'
          width='max-content'
        >
          {getArchivedLabel(getValue()?.name, getValue()?.archived)}
        </Text>
      </Tooltip>
    ),
  }),
  columnHelper.accessor('floorName', {
    header: ({ header }) => (
      <HeaderCell
        canSort={header.column.getCanSort()}
        getIsSorted={header.column.getIsSorted}
        label='Floor'
      />
    ),
    cell: ({ getValue }) => (
      <TableCell
        dataTestId='incidentsPage_table_floorCell'
        fontWeight={400}
        label={getValue()}
        maxWidth='200px'
      />
    ),
    enableSorting: true,
  }),
  columnHelper.accessor('incidentStatusOwner', {
    header: ({ header }) => (
      <HeaderCell
        canSort={header.column.getCanSort()}
        getIsSorted={header.column.getIsSorted}
        label='Status'
      />
    ),
    cell: ({ getValue }) => <Status incidentStatusOwner={getValue()} />,
    enableSorting: true,
  }),
  columnHelper.accessor('source', {
    header: ({ header }) => (
      <HeaderCell
        canSort={header.column.getCanSort()}
        getIsSorted={header.column.getIsSorted}
        label='Source'
      />
    ),
    cell: ({ getValue }) => (
      <Tooltip
        hasArrow
        label={getIncidentSourceDisplayName(getValue())}
        openDelay={500}
        placement='auto'
        textTransform='capitalize'
      >
        <HStack>
          <Box minW='20px'>
            <Source source={getValue()} />
          </Box>
          <Text
            color='black'
            fontSize='14px'
            fontWeight={400}
            maxWidth='250px'
            noOfLines={2}
            whiteSpace='pre-line'
            width='max-content'
          >
            {getValue()?.name}
          </Text>
        </HStack>
      </Tooltip>
    ),
    enableSorting: false,
  }),
  columnHelper.accessor('operator', {
    header: ({ header }) => (
      <HeaderCell
        canSort={header.column.getCanSort()}
        getIsSorted={header.column.getIsSorted}
        label='Operator'
      />
    ),
    cell: ({ getValue }) => (
      <Tooltip
        hasArrow
        label={getValue() ?? 'No operator'}
        openDelay={500}
        placement='auto'
        textTransform='capitalize'
      >
        {getValue() === 'AI Operator' ? (
          <Avatar
            backgroundColor='white'
            data-testid='operator-cell'
            icon={<AiOperator />}
            size='xs'
          />
        ) : (
          <Avatar data-testid='operator-cell' name={getValue()} size='xs' />
        )}
      </Tooltip>
    ),
    maxSize: 20,
  }),
  columnHelper.accessor('guard', {
    header: ({ header }) => (
      <HeaderCell
        canSort={header.column.getCanSort()}
        getIsSorted={header.column.getIsSorted}
        label='Guard'
      />
    ),
    cell: ({ getValue }) => <IncidentGuardCell incident={getValue()} />,
    maxSize: 20,
  }),
]

/**
 * This is a hacky, but working solution to a problem encountered during the implementation of Bulk Action
 * within the Incidents table. The main issue was that (seemingly) every table across the portal was setup with the
 * understanding that if _any_ part of a table row gets clicked on, we want the row's onClick logic to run.
 * This meant that if somebody just barely mis-clicked and missed the newly added checkbox on Incidents table,
 * instead of nothing happening, they'd have the Incident drawer slide out b/c _technically_ they missed the checkbox
 * and clicked the table's row.
 *
 * With the addition of the `ignoreClicksToCell` meta-data flag, we should be able to define at a column level if we
 * want clicks to that column to be ignored, and then at the Table level we can ignore clicks to that column.
 * Maybe there's a better solution, but I think the best solution here, but for the time being this works and I don't
 * want this to further block this feature.
 */
declare module '@tanstack/react-table' {
  // eslint-disable-next-line
  interface ColumnMeta<TData extends RowData, TValue> {
    ignoreClicksToCell?: boolean
  }
}
